import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  IonInput,
  IonButton,
  IonIcon,
  IonSpinner,
} from '@ionic/react';
import { eyeOutline, eyeOffOutline, lockClosedOutline } from 'ionicons/icons';
import { ImpactStyle } from '@capacitor/haptics';
import ProSectionHeader from '../../components/pro/ProSectionHeader';
import ProCard from '../../components/pro/ProCard';
import {
  useProToolbarEnd,
  useProToolbarSticky,
} from '../../components/pro/ProToolbarSlot';
import api from '../../lib/api';
import { useCapacitorPlatform } from '@shared/hooks/useCapacitorPlatform';
import { useProToast, navStateWithToast } from './useProToast';
import { newPasswordsMatch, passwordChangeFields, PASSWORD_HINT } from '../../components/auth/credentialSchema';
import './ProAccount.css';

// Rules from components/auth/credentialSchema — one minimum, app-wide.
const passwordSchema = z
  .object(passwordChangeFields)
  .refine(newPasswordsMatch.check, newPasswordsMatch.options);

type PasswordFormData = z.infer<typeof passwordSchema>;

const ProProfileSecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useProToast();
  const { triggerHaptic } = useCapacitorPlatform();
  const [visible, setVisible] = useState<Record<keyof PasswordFormData, boolean>>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation(
    async (values: PasswordFormData) => {
      const { data } = await api.put('/users/password', values);
      return data;
    },
    {
      onSuccess: () => {
        // Reset is a no-op once we navigate away, but keep it defensively in
        // case the route change is cancelled (e.g. unsaved-changes guard).
        reset({ currentPassword: '', newPassword: '', confirmPassword: '' });
        triggerHaptic(ImpactStyle.Light);
        navigate('/pro/profile', {
          state: navStateWithToast({
            message: 'Password updated.',
            color: 'success',
          }),
        });
      },
      onError: (err: any) => {
        showError(err?.response?.data?.error || 'Could not update password.');
      },
    }
  );

  const onSubmit = (values: PasswordFormData) => mutation.mutate(values);

  // Scroll the first visible error message into view after a failed Save.
  // Deferred to next frame so RHF has flipped `errors` and React has
  // rendered the `.pro-account__field-error` elements.
  const onInvalid = useCallback((_errors: Record<string, unknown>) => {
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('.pro-account__field-error')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  // Pin the toolbar to the top for this page's lifetime.
  useProToolbarSticky(true);

  // Render the Save button into the toolbar's right slot.
  useProToolbarEnd(
    <IonButton
      onClick={handleSubmit(onSubmit, onInvalid)}
      disabled={!isDirty || mutation.isLoading}
      className="text-white font-semibold"
    >
      {mutation.isLoading ? (
        <IonSpinner name="crescent" className="text-white" />
      ) : (
        'Save'
      )}
    </IonButton>,
    [isDirty, mutation.isLoading, handleSubmit, onInvalid],
  );

  const toggle = (key: keyof PasswordFormData) =>
    setVisible((v) => ({ ...v, [key]: !v[key] }));

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="pro-account pb-8">
        <ProSectionHeader title="Change password" icon={lockClosedOutline} />
        <div className="px-2">
          <ProCard>
            <PasswordField
              label="Current password"
              visible={visible.currentPassword}
              onToggle={() => toggle('currentPassword')}
              error={errors.currentPassword?.message}
            >
              <Controller
                control={control}
                name="currentPassword"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    type={visible.currentPassword ? 'text' : 'password'}
                    autocomplete="current-password"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </PasswordField>
            <PasswordField
              label="New password"
              visible={visible.newPassword}
              onToggle={() => toggle('newPassword')}
              error={errors.newPassword?.message}
            >
              <Controller
                control={control}
                name="newPassword"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    type={visible.newPassword ? 'text' : 'password'}
                    autocomplete="new-password"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </PasswordField>
            <PasswordField
              label="Confirm new password"
              visible={visible.confirmPassword}
              onToggle={() => toggle('confirmPassword')}
              error={errors.confirmPassword?.message}
            >
              <Controller
                control={control}
                name="confirmPassword"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    type={visible.confirmPassword ? 'text' : 'password'}
                    autocomplete="new-password"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </PasswordField>
          </ProCard>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2 px-3 pb-2">
            {PASSWORD_HINT} Pick something unique to Capitol Commons.
          </p>
        </div>
      </form>
    </>
  );
};

interface PasswordFieldProps {
  label: string;
  visible: boolean;
  onToggle: () => void;
  error?: string;
  children: React.ReactNode;
}

const PasswordField: React.FC<PasswordFieldProps> = ({ label, visible, onToggle, error, children }) => (
  <div className="pro-account__field">
    <span className="pro-account__field-label">{label}</span>
    <div style={{ position: 'relative' }}>
      {children}
      <button
        type="button"
        onClick={onToggle}
        aria-label={visible ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute',
          right: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 0,
          padding: 6,
          color: '#76647c',
          cursor: 'pointer',
        }}
      >
        <IonIcon icon={visible ? eyeOffOutline : eyeOutline} style={{ fontSize: 18 }} />
      </button>
    </div>
    {error && <p className="pro-account__field-error">{error}</p>}
  </div>
);

export default ProProfileSecurityPage;
